// Web treatment for the per-task lock / unlock template.
//
// 69 task screens (`*LockUnlockScreen.tsx`) share one layout: a title,
// then a navy message block and a grey block holding the two big buttons,
// both `position: 'absolute'` and pinned to the bottom of the screen so
// they rise from the bottom of a phone, with the grey block overlapping
// the navy one. In a tablet or desktop column the screen is viewport-tall
// and 600–880px wide, so the pinned blocks sink to the bottom, leave a
// void under the title, and the buttons stretch to the full column width.
//
// On those frames this puts the two blocks back into normal flow under
// the title, keeps the overlap, rounds the navy block into a card, and
// caps both at a comfortable width. The phone frame (< 768px) keeps the
// original pinned layout, as a device would. Applied inside the
// `react-native` shim's `StyleSheet.create`, like sheetStyle.ts, so none
// of the 69 files change and mobile never sees it.
//
// Matched by style-key signature only: a sheet with `blueContainer`,
// `grayButtonContainer`, `lockButton` and `unlockButton`, where the navy
// block is absolutely positioned. Anything else passes through untouched.

import { layoutModeFor } from './useLayoutMode';

type Style = Record<string, unknown>;

/** Widest the message and button blocks get in a wide column. */
export const LOCK_UNLOCK_MAX_WIDTH = 560;

function isLockUnlockSheet(styles: Record<string, unknown>): boolean {
  const blue = styles.blueContainer as Style | undefined;
  return (
    !!blue &&
    typeof blue === 'object' &&
    blue.position === 'absolute' &&
    'grayButtonContainer' in styles &&
    'lockButton' in styles &&
    'unlockButton' in styles
  );
}

function widthNow(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth;
}

export function relayoutLockUnlock<T extends Record<string, unknown>>(
  styles: T,
  viewportWidth: number = widthNow(),
): T {
  if (!isLockUnlockSheet(styles)) return styles;
  if (layoutModeFor(viewportWidth) === 'phone') return styles;

  const out: Record<string, unknown> = { ...styles };
  const blue = styles.blueContainer as Style;
  const gray = styles.grayButtonContainer as Style;
  const radius = (blue.borderTopLeftRadius as number | undefined) ?? 30;

  out.blueContainer = {
    ...blue,
    position: 'relative',
    bottom: undefined,
    left: undefined,
    right: undefined,
    alignSelf: 'center',
    width: '100%',
    maxWidth: LOCK_UNLOCK_MAX_WIDTH,
    marginTop: 28,
    // Enough for the grey block to overlap, as on the phone, without the
    // phone's 180px of run-off-the-bottom padding.
    paddingBottom: 76,
    borderRadius: radius,
  };
  out.grayButtonContainer = {
    ...gray,
    position: 'relative',
    bottom: undefined,
    left: undefined,
    right: undefined,
    alignSelf: 'center',
    width: '100%',
    maxWidth: LOCK_UNLOCK_MAX_WIDTH,
    marginTop: -52,
    paddingBottom: 28,
  };

  // The column that held the pinned blocks spreads its children across the
  // viewport-tall frame; in flow the blocks should follow the title.
  for (const key of Object.keys(styles)) {
    if (key === 'blueContainer' || key === 'grayButtonContainer') continue;
    const s = styles[key] as Style | undefined;
    if (s && typeof s === 'object' && s.flex === 1 && s.justifyContent === 'space-between') {
      out[key] = { ...s, justifyContent: 'flex-start' };
    }
  }
  return out as T;
}
